import { format } from 'date-fns';
import { daysOfWeek, months, moreFunctions } from '../constants/scheduleDates';

/**
 * Извлекает значение из строки формата "key=value;...".
 * @param {string} input - Входная строка.
 * @param {string} key - Ключ для поиска.
 * @returns {string|null} Найденное значение или null.
 */
function extractValueFromString(input, key) {
  const regex = new RegExp(`\\b${key}=([^;]+)`);
  const match = input.match(regex);
  return match ? match[1] : null;
}

/**
 * Преобразует ID дней недели (1,2,3) в русские названия.
 * @param {string} days - Строка с ID дней через запятую.
 * @returns {string} Названия дней через запятую.
 */
function mapWeekDays(days) {
  if (!days) return '';

  const weekDaysMap = daysOfWeek.reduce((acc, day) => ({ ...acc, [day.id]: day.value }), {});
  return days
    ?.split(',')
    .map((d) => weekDaysMap[d])
    .join(', ');
}

/**
 * Преобразует конфигурацию дней месяца (числа, порядковые дни) в человекочитаемый формат.
 * @param {string} days - Дни месяца через запятую.
 * @param {string} order - Порядковый номер (LAST_SUN, FIRST_MON и т.д.).
 * @param {string} defDays - Тип дня (DAY_OF_MONTH, WEN, FRI и т.д.).
 * @returns {string} Человекочитаемая строка дней месяца.
 */
function mapMonthDays(days, order, defDays) {
  if (!days && !order && !defDays) return '';

  const weekDaysMap = daysOfWeek.reduce((acc, day) => ({ ...acc, [day.id]: day.value }), {});
  if (order) {
    const moreFunc = moreFunctions.reduce(
      (acc, m) => ({
        ...acc,
        [m.id]: { m: m.short, f: m.shortFm, md: m.shortMd },
      }),
      {},
    );
    if (defDays !== 'DAY_OF_MONTH') {
      const weekDay = weekDaysMap[defDays];
      return `${weekDay}(${
        moreFunc[order][
          defDays === 'WEN' || defDays === 'FRI' || defDays === 'SAT' ? 'f' : defDays === 'SUN' ? 'md' : 'm'
        ]
      })`;
    }
    return `${moreFunc[order].m} день месяца`;
  }
  return days
    ?.split(',')
    .map((d) => +d)
    .sort((a, b) => a - b)
    .join(', ');
}

/**
 * Преобразует ID месяцев в русские названия с опциональным указанием дней.
 * @param {string} mth - ID месяцев через запятую.
 * @param {string} order - Порядковый номер дня.
 * @param {string} defDays - Тип дня.
 * @returns {string} Человекочитаемая строка месяцев.
 */
function mapMonth(mth, order, defDays) {
  if (!mth) return '';

  const monthMap = months.reduce((acc, m) => ({ ...acc, [m.id]: m.short }), {});
  const weekDay = order && defDays ? ` - ${mapMonthDays(null, order, defDays)}` : '';
  return `${mth
    ?.split(',')
    .map((month) => monthMap[month])
    .join(', ')}${weekDay}`;
}

/**
 * Парсит строку расписания (TYPE=EVERYWEEK;DAYS=1,3;UNTIL=...) в человекочитаемый текст на русском.
 * @param {string} value - Строка расписания в формате key=value;...
 * @returns {string} Человекочитаемое описание расписания.
 */
function parseSchedule(value) {
  if (!value) {
    return '-';
  }
  const type = extractValueFromString(value, 'TYPE');
  const defEachN = extractValueFromString(value, 'EACH_N');
  const defDays = extractValueFromString(value, 'DAYS');
  const defMonth = extractValueFromString(value, 'MONTH');
  const defMonthDays = extractValueFromString(value, 'MONTH_DAYS');
  const order = extractValueFromString(value, 'ORDER');
  let until = extractValueFromString(value, 'UNTIL') || '';

  const isNRepeat = defEachN > 1;

  if (until) {
    until = `, до ${format(new Date(until), 'dd.MM.yyyy')}`;
  }
  switch (type) {
    case 'EVERYDAY':
      return `${isNRepeat ? `Каждый ${defEachN} день` : 'Ежедневно'}${until}`;
    case 'EVERYWEEK':
      if (!defDays && !isNRepeat) return `Еженедельно${until}`;
      return `${mapWeekDays(defDays)}${isNRepeat ? ` каждую ${defEachN} неделю` : ''}${until}`;
    case 'EVERYMONTH':
      if (!defMonthDays && !order && !defDays && !isNRepeat) return `Ежемесячно${until}`;
      return `${mapMonthDays(defMonthDays, order, defDays)}${isNRepeat ? ` каждый ${defEachN} месяц` : ''}${until}`;
    case 'EVERYYEAR':
      if (!defMonthDays && !order && !defDays && !isNRepeat) return `Ежегодно${until}`;
      return `${mapMonth(defMonth, order, defDays)}${isNRepeat ? ` каждый ${defEachN} год` : ''}${until}`;
    case 'NORETRY':
      return 'Один раз';
    default:
      return value;
  }
}

export default parseSchedule;
